import { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/shadcn/button';
import {
  ITrainersData,
  ITrainersPage,
  ITrainersTool,
  ITrainerSubmit,
} from '@/types/trainers';
import {
  getTrainersContent,
  submitBenchmark,
  submitTrainersResponse,
} from '@/api/qa';
import { useTrainerFeedback } from '@/hooks/useTrainerFeedback';

import Feedback from '@/components/ui/trainers/shared/Feedback';
import { useTrainerReviews } from '@/hooks/useTrainerReviews';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import TrainerHeader from './TrainerHeader';
import TrainerFooter from './TrainerFooter';
import TrainerController, {
  TrainerButtons,
  TrainerInputs,
  TrainerSelect,
} from './TrainerController';
import ErrorMessage from './shared/ErrorMessage';
import SelectableImageGrid from './SelectableImageGrid';
interface Props extends ITrainersPage {
  data?: ITrainersData;
  tool?: ITrainersTool;
  modifyPosition?: boolean;
  modifyAzimuth?: boolean;
}

const TOLL_PRICE_STRUCTURE: Record<string, { label: string; type: string }> = {
  location: { label: 'Location', type: 'string' },
  passengers: { label: 'Number of Passengers', type: 'number' },
  price: { label: 'Toll Price', type: 'number' },
};

const TollPriceReading: React.FC<Props> = ({
  data: initialData,
  page,
  name,
  description: initialDescription,
  location,
  tool,
}) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const controllerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const toolId = initialData?.toolId || '';
  const [data, setData] = useState<ITrainersData | undefined>(initialData);
  const { incrementReviews } = useTrainerReviews();
  const { leaseId, phiLandmarkObectDetections = [] } = data?.content || {};
  const [underConstruction, setUnderConstruction] = useState<boolean>(false);

  const description =
    initialDescription ||
    (data?.content
      ? `Fill out the toll price info for different categories based on the images of the toll price sign, if you cant detect the prices leave as empty.`
      : '');

  const isDataReady = Boolean(data?.content);

  const [isBenchmark, setIsBenchmark] = useState(false);

  const { feedback, setFeedback } = useTrainerFeedback();

  const [tollPriceInfo, setTollPriceInfo] = useState<
    Record<string, number | null | string>[]
  >([]);

  const clearAllData = () => {
    setUnderConstruction(false);
    setData(undefined);
    setIsBenchmark(false);
    setTollPriceInfo([]);
  };

  const refetchClient = useCallback(
    async (id: string) => {
      const _data: ITrainersData = await getTrainersContent(id, page);
      setData(_data);
    },
    [page],
  );

  const onBenchmarkToggle = (checked: boolean) => {
    setIsBenchmark(checked);
  };
  const onSubmit = async ({ outcome, value }: ITrainerSubmit) => {
    clearAllData();
    if (!leaseId) {
      return;
    }
    const sending = {
      leaseId,
      outcome: outcome ?? value,
      value: JSON.stringify({
        tollPriceInfo: tollPriceInfo,
        underConstruction,
      }),
    };

    if (isBenchmark) {
      await submitBenchmark(toolId, sending);
    }

    const { error, benchmarkFeedback } = await submitTrainersResponse(
      toolId,
      sending,
    );

    if (benchmarkFeedback) {
      setFeedback({
        ...benchmarkFeedback,
        instructionsLink: tool?.args?.instructions,
      });
    }
    if (error) {
      setData({ error });
    } else {
      incrementReviews(toolId);
      refetchClient(toolId);
    }
  };
  const addRow = () => {
    setTollPriceInfo([
      ...tollPriceInfo,
      Object.fromEntries(
        Object.keys(TOLL_PRICE_STRUCTURE).map(key => [key, null]),
      ),
    ]);
  };
  const removeRow = (index: number) => {
    setTollPriceInfo(tollPriceInfo.filter((_, i) => i !== index));
  };
  const onChange = (e: any, index: number, key: string) => {
    setTollPriceInfo(curTollPriceInfo => {
      const value =
        !e.target.value || !e.target.value.length ? null : e.target.value;
      const newObj = [...curTollPriceInfo];
      newObj[index][key] =
        value && TOLL_PRICE_STRUCTURE[key].type == 'number'
          ? Number(value)
          : value;
      return newObj;
    });
  };
  return (
    <div className="flex size-full flex-col">
      <TrainerHeader
        data={data}
        name={name}
        description={description}
        location={location}
        tool={tool}
        elementRef={headerRef}
      />
      {data?.error ? (
        <ErrorMessage error={data.error} />
      ) : (
        <>
          <SelectableImageGrid
            images={phiLandmarkObectDetections.map(o => o.url)}
            bboxes={phiLandmarkObectDetections.map(o => o.bbox)}
            width={phiLandmarkObectDetections.length}
            height={1}
            selectable={false}
          />

          <div className="flex flex-col items-center justify-center gap-2 p-2">
            <div className="flex flex-row gap-1 p-2">
              <Label>Under Construction</Label>
              <input
                type="checkbox"
                checked={underConstruction}
                onChange={e => {
                  setUnderConstruction(e.target.checked);
                }}
              />
            </div>
            {tollPriceInfo.map((row, index) => (
              <div className="flex items-end justify-end gap-2" key={index}>
                {Object.keys(row).map((key: string) => (
                  <div key={key} className="inline">
                    <Label>{TOLL_PRICE_STRUCTURE[key].label}</Label>
                    <Input
                      placeholder={TOLL_PRICE_STRUCTURE[key].label}
                      value={row[key] ?? ''}
                      type={TOLL_PRICE_STRUCTURE[key].type}
                      onChange={(e: any) => {
                        onChange(e, index, key);
                      }}
                    />
                  </div>
                ))}
                <Button
                  onClick={() => {
                    removeRow(index);
                  }}>
                  Remove Row
                </Button>
              </div>
            ))}
            <Button onClick={addRow}>Add Row</Button>
          </div>
          <TrainerController
            containerRef={controllerRef}
            isDataReady={isDataReady}
            onSubmit={onSubmit}
            data={data}
            tool={tool}>
            <TrainerButtons />
            <TrainerSelect />
            <TrainerInputs />
          </TrainerController>
        </>
      )}
      <TrainerFooter
        data={data}
        elementRef={footerRef}
        onBenchmarkToggle={onBenchmarkToggle}
        isBenchmark={isBenchmark}
      />
      {feedback?.feedbackMessage && <Feedback />}
    </div>
  );
};

export default TollPriceReading;
